import { useEffect, useMemo, useRef, useState } from 'react';
import Icon          from '../components/ui/Icon';
import PullToRefresh from '../components/ui/PullToRefresh';
import { useApp }    from '../context/AppContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNotificationsList,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useUnreadCount,
  NOTIFICATIONS_KEY,
  UNREAD_COUNT_KEY,
} from '../hooks/useNotifications';

/* ── Filter tabs ──────────────────────────────────────────────────────────── */
const FILTERS = [
  { key: 'all',    label: 'All'    },
  { key: 'unread', label: 'Unread' },
  { key: 'read',   label: 'Read'   },
];

/* ── Relative time formatter ──────────────────────────────────────────────── */
const fmtRelative = (iso) => {
  if (!iso) return '';
  const d       = new Date(iso);
  const diffMs  = Date.now() - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1)  return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH   = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH}h ago`;
  const diffD   = Math.floor(diffH / 24);
  if (diffD < 7)    return `${diffD}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const isReadFlag = (n) => Boolean(n.is_read);

/* ── Pick an icon for the row based on the data payload ───────────────────── */
const iconForNotification = (n) => {
  const data = n?.data ?? {};
  if (data.request_id) return 'list';
  if (data.ticket_id)  return 'ticket';
  if (data.host_id)    return 'shieldCheck';
  if (data.room_number) return 'building';
  return 'bell';
};

/* ── Loading skeleton ─────────────────────────────────────────────────────── */
const ListSkeleton = () => (
  <div className="px-5 flex flex-col gap-3 pt-2">
    {[1, 2, 3, 4].map((n) => (
      <div key={n} className="bg-surface-800 rounded-2xl p-4 border border-surface-500 card-shadow">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl shimmer-bg flex-shrink-0" />
          <div className="flex-1">
            <div className="h-3.5 w-44 shimmer-bg rounded mb-2" />
            <div className="h-3 w-56 shimmer-bg rounded mb-2" />
            <div className="h-2.5 w-20 shimmer-bg rounded" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ── Empty state ──────────────────────────────────────────────────────────── */
const EmptyState = ({ filter }) => (
  <div
    className="flex flex-col items-center justify-center py-16 text-center px-8 animate-fadeUp"
    style={{ animationFillMode: 'forwards' }}
  >
    <div className="w-16 h-16 rounded-2xl bg-surface-700 border border-surface-500 flex items-center justify-center mb-4">
      <Icon name="bell" size={26} className="text-surface-300" />
    </div>
    <p className="font-syne font-bold text-slate-800 text-base mb-1">
      {filter === 'unread' ? 'No unread notifications' :
       filter === 'read'   ? 'No read notifications yet' :
                             'No notifications yet'}
    </p>
    <p className="font-dm text-sm text-surface-200">
      We&rsquo;ll let you know when something needs your attention.
    </p>
  </div>
);

/* ── Error state ──────────────────────────────────────────────────────────── */
const ErrorState = ({ message, onRetry }) => (
  <div className="px-5 flex flex-col items-center justify-center py-16 text-center">
    <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center mb-3">
      <Icon name="help" size={22} className="text-rose-400" />
    </div>
    <p className="font-dm text-sm text-surface-200 mb-3">{message || 'Something went wrong.'}</p>
    {onRetry && (
      <button onClick={onRetry} className="font-dm text-xs text-brand-600 font-semibold">
        Try again
      </button>
    )}
  </div>
);

/* ── Single notification row ──────────────────────────────────────────────── */
const NotificationRow = ({ n, delay, onTap }) => {
  const read = isReadFlag(n);
  const icon = iconForNotification(n);

  return (
    <button
      onClick={() => onTap(n)}
      className={`
        w-full text-left rounded-2xl p-4 border card-shadow press-scale
        animate-fadeUp transition-colors
        ${read
          ? 'bg-surface-800 border-surface-500'
          : 'bg-brand-50/40 border-brand-200'}
      `}
      style={{ animationDelay: delay, animationFillMode: 'forwards', opacity: 0 }}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            read ? 'bg-surface-700 border border-surface-500' : 'bg-brand-50 border border-brand-200'
          }`}
        >
          <Icon name={icon} size={18} className={read ? 'text-surface-300' : 'text-brand-600'} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <p className={`font-syne text-sm leading-snug flex-1 ${read ? 'font-semibold text-slate-700' : 'font-bold text-slate-800'}`}>
              {n.title}
            </p>
            {!read && (
              <span className="w-2 h-2 rounded-full bg-brand-500 flex-shrink-0 mt-1.5" />
            )}
          </div>
          {n.body && (
            <p className="font-dm text-xs text-surface-200 mt-1 leading-relaxed line-clamp-3">
              {n.body}
            </p>
          )}
          <p className="font-dm text-[11px] text-surface-300 mt-2">
            {fmtRelative(n.created_at)}
          </p>
        </div>
      </div>
    </button>
  );
};

/* ── Filter chips ─────────────────────────────────────────────────────────── */
const FilterChips = ({ active, onChange }) => (
  <div className="flex gap-2 overflow-x-auto flex-1 pb-1" style={{ scrollbarWidth: 'none' }}>
    {FILTERS.map((f) => {
      const isActive = active === f.key;
      return (
        <button
          key={f.key}
          onClick={() => onChange(f.key)}
          className="flex-shrink-0 px-3.5 py-2 rounded-full font-dm font-semibold text-xs transition-all duration-200"
          style={
            isActive
              ? { background: 'linear-gradient(135deg, #4338ca, #6366f1)', color: '#fff', border: '1px solid transparent' }
              : { background: 'var(--surface-800, #f8fafc)', color: 'var(--surface-200, #94a3b8)', border: '1px solid var(--surface-500, #e2e8f0)' }
          }
        >
          {f.label}
        </button>
      );
    })}
  </div>
);

/* ── Main page ────────────────────────────────────────────────────────────── */
const NotificationsPage = () => {
  const { setActiveTab, setPreselectedRequestId, setPreselectedTicketId } = useApp();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all');

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotificationsList({ filter });

  const { mutate: markRead }    = useMarkNotificationRead();
  const { mutate: markAllRead, isPending: isMarkingAll } = useMarkAllNotificationsRead();
  const { unread } = useUnreadCount();

  // Refresh on screen focus (whenever this page mounts / filter changes)
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  }, [queryClient]);

  const items = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.data ?? []),
    [data]
  );
  const total = data?.pages?.[0]?.total ?? 0;

  /* ── Infinite scroll sentinel ─────────────────────────────────────────── */
  const sentinelRef = useRef(null);
  const hasNextRef  = useRef(hasNextPage);
  const fetchingRef = useRef(isFetchingNextPage);
  useEffect(() => { hasNextRef.current  = hasNextPage; }, [hasNextPage]);
  useEffect(() => { fetchingRef.current = isFetchingNextPage; }, [isFetchingNextPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextRef.current && !fetchingRef.current) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage]);

  /* ── Tap row → mark read + deep-link ──────────────────────────────────── */
  const handleTap = (n) => {
    if (!isReadFlag(n)) {
      markRead(n.id);
    }

    const d = n?.data ?? {};
    if (d.ticket_id) {
      setPreselectedTicketId?.(d.ticket_id);
      setActiveTab('tickets');
      return;
    }
    if (d.request_id) {
      setPreselectedRequestId?.(d.request_id);
      setActiveTab('requests');
      return;
    }
    if (d.room_number) {
      // New guest service request — surface in requests list
      setActiveTab('requests');
      return;
    }
    if (d.host_id) {
      // Approval / rejection — drop user to dashboard
      setActiveTab('dashboard');
      return;
    }
    // Unknown payload — stay on the screen.
  };

  return (
    <PullToRefresh onRefresh={() => refetch()}>
      <div className="pb-6">
        {/* Header row: filters + Mark all read */}
        <div className="pt-4 pb-3 animate-fadeUp" style={{ animationFillMode: 'forwards' }}>
          <div className="flex items-center gap-2 px-5">
            <FilterChips active={filter} onChange={setFilter} />
            <button
              onClick={() => markAllRead()}
              disabled={isMarkingAll || unread === 0}
              className="flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full font-dm font-semibold text-xs text-white press-scale disabled:opacity-40 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #4338ca, #6366f1)' }}
            >
              <Icon name="check" size={13} className="text-white" strokeWidth="2.5" />
              {isMarkingAll ? 'Marking…' : 'Mark all'}
            </button>
          </div>
        </div>

        {/* Total / unread summary */}
        {total > 0 && (
          <div className="px-5 pb-2">
            <span className="font-dm text-xs text-surface-200">
              {total} notification{total !== 1 ? 's' : ''}
              {unread > 0 ? ` · ${unread} unread` : ''}
            </span>
          </div>
        )}

        {/* Loading */}
        {isLoading && items.length === 0 && <ListSkeleton />}

        {/* Error */}
        {isError && !isLoading && (
          <ErrorState message={error?.message} onRetry={refetch} />
        )}

        {/* Empty */}
        {!isLoading && !isError && items.length === 0 && (
          <EmptyState filter={filter} />
        )}

        {/* List */}
        {!isError && items.length > 0 && (
          <div className="px-5 flex flex-col gap-3">
            {items.map((n, i) => (
              <NotificationRow
                key={n.id}
                n={n}
                delay={`${Math.min(i, 8) * 0.05}s`}
                onTap={handleTap}
              />
            ))}
          </div>
        )}

        {/* Infinite-scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />

        {/* Inline spinner while fetching additional pages */}
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <span className="w-5 h-5 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </PullToRefresh>
  );
};

export default NotificationsPage;
